import React, { useState, useMemo } from 'react'
import { Theme, makeStyles } from '@material-ui/core'
import {
    TextField,
    Card,
    CardContent,
    Typography,
    Grid,
    Container,
    Box,
    IconButton,
    Tooltip,
    Popover,
    Button,
} from '@material-ui/core'
import { Edit as EditIcon } from '@material-ui/icons'
import { EntityClass } from '../../../models/DocumentAudit/types'
import { getEntityClassificationUiName } from '../../../models/DocumentAudit/services'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2),
    },
    searchContainer: {
        marginBottom: theme.spacing(3),
    },
    cardsContainer: {
        flex: 1,
        overflow: 'auto',
        padding: theme.spacing(1),
    },
    card: {
        marginBottom: theme.spacing(2),
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    dataType: {
        color: theme.palette.text.primary,
        fontWeight: 500,
    },
    description: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    popoverContent: {
        padding: theme.spacing(2),
        minWidth: 300,
    },
    popoverActions: {
        marginTop: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
}))

interface Props {
    theme: Theme
    isActive: boolean
    entityClasses: EntityClass[]
    updateEntityClassDescription: (
        entityClassId: number,
        description: string,
    ) => void
}

export const EntityClassConfig = ({
    theme,
    isActive,
    entityClasses,
    updateEntityClassDescription,
}: Props) => {
    const classes = useStyles()
    const [searchQuery, setSearchQuery] = useState('')
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [
        editingEntityClass,
        setEditingEntityClass,
    ] = useState<EntityClass | null>(null)
    const [editedDescription, setEditedDescription] = useState('')

    const filteredEntityClasses = useMemo(() => {
        return entityClasses.filter(
            (entityClass) =>
                entityClass.name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                entityClass.description
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
        )
    }, [entityClasses, searchQuery])

    const handleEditClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        entityClass: EntityClass,
    ) => {
        setAnchorEl(event.currentTarget)
        setEditingEntityClass(entityClass)
        setEditedDescription(entityClass.description)
    }

    const handleClose = () => {
        setAnchorEl(null)
        setEditingEntityClass(null)
        setEditedDescription('')
    }

    const handleSave = () => {
        if (editingEntityClass) {
            updateEntityClassDescription(
                editingEntityClass.id,
                editedDescription,
            )
        }
        handleClose()
    }

    const open = Boolean(anchorEl)

    if (!isActive) return null

    return (
        <Container className={classes.root}>
            <Box className={classes.searchContainer}>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Search Entity Classes"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or description..."
                />
            </Box>

            <Box className={classes.cardsContainer}>
                {filteredEntityClasses.map((entityClass) => (
                    <Card className={classes.card} key={entityClass.id}>
                        <CardContent>
                            <div className={classes.cardHeader}>
                                <div className={classes.headerLeft}>
                                    <Typography variant="h6" component="h2">
                                        {getEntityClassificationUiName(
                                            entityClass.name,
                                        )}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        className={classes.dataType}
                                    >
                                        {entityClass.data_type}
                                    </Typography>
                                </div>
                                <Tooltip title="Edit Description">
                                    <IconButton
                                        size="small"
                                        onClick={(e) =>
                                            handleEditClick(e, entityClass)
                                        }
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>

                            <Typography
                                variant="body2"
                                className={classes.description}
                            >
                                {entityClass.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <div className={classes.popoverContent}>
                    <Typography variant="subtitle1" gutterBottom>
                        Edit Description for{' '}
                        {editingEntityClass &&
                            getEntityClassificationUiName(
                                editingEntityClass.name,
                            )}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                    />
                    <div className={classes.popoverActions}>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Popover>
        </Container>
    )
}
